import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { CheckCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await fetch(`${API}/payments/status/${sessionId}`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === "paid") {
          setStatus("success");
          await checkAuth(); // Refresh user data
        } else if (data.status === "expired") {
          setStatus("expired");
        } else {
          // Still pending, poll again
          setAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        }
      }
    } catch (error) {
      console.error("Error checking payment:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const handleContinue = () => {
    if (user?.role === "recruiter") {
      navigate("/dashboard/recruiter");
    } else {
      navigate("/dashboard/jobseeker");
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      <motion.div 
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {status === "loading" && (
          <>
            <div className="w-20 h-20 bg-crimson/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-crimson animate-spin" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">PROCESSING PAYMENT</h1>
            <p className="text-gray-400 mb-8">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div 
              className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring" }}
            >
              <CheckCircle className="w-10 h-10 text-green-500" />
            </motion.div>
            <h1 className="font-heading text-3xl font-bold mb-4">PAYMENT SUCCESSFUL!</h1>
            <p className="text-gray-400 mb-8">
              Your subscription has been activated. You now have access to all premium features.
            </p>
            <Button
              onClick={handleContinue}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8 py-6"
              data-testid="continue-btn"
            >
              Continue to Dashboard
            </Button>
          </>
        )}

        {status === "timeout" && (
          <>
            <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-yellow-500" />
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">VERIFICATION IN PROGRESS</h1>
            <p className="text-gray-400 mb-8">
              Payment verification is taking longer than expected. Please check your email for confirmation.
            </p>
            <Button
              onClick={handleContinue}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8 py-6"
            >
              Continue to Dashboard
            </Button>
          </>
        )}

        {status === "expired" && (
          <>
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">✕</span>
            </div>
            <h1 className="font-heading text-3xl font-bold mb-4">SESSION EXPIRED</h1>
            <p className="text-gray-400 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Button
              onClick={() => navigate("/subscription")}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8 py-6"
            >
              Back to Pricing
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
